import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { HoursType } from '@app/shared/types/enums/hours-type';
import { RegistrationType } from '@app/shared/types/enums/registration-type';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { WorkTimeRegistration } from '@app/shared/types/models/reports/work-time-registration';
import { Task } from '@app/shared/types/models/task/task.model';
import { formatDateTimeInTimeZone, formatTimeInTimeZone } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';

export interface TimeRegistrationDetailRow {
  readonly id: string;
  readonly labelKey: string;
  readonly value?: string;
  readonly valueKey?: string;
}

const HOURS_TYPE_LABEL_KEYS: Record<HoursType, string> = {
  Auto: 'time-report-hour-type-ordinary-auto',
  OrdinaryHours: 'time-report-hour-type-ordinary-hours',
  BankPlus: 'time-report-hour-type-bank_plus',
  BankMinus: 'time-report-hour-type-bank_minus',
  Overtime50: 'time-report-hour-type-overtime_50',
  Overtime100: 'time-report-hour-type-overtime_100',
  Manual: 'time-report-hour-type-manual'
};

const REGISTRATION_TYPE_LABEL_KEYS: Record<RegistrationType, string> = {
  Timer: 'time-reports-form-modal-timer',
  Manual: 'time-reports-form-modal-manual',
  Range: 'time-reports-form-modal-range',
  Absence: 'time-registration-details.registration-type-absence'
};

/**
 * Read-only summary of the work time registration behind a time report, rendered as a
 * collapsed expansion panel.
 *
 * Rows depend on the registration type: manual registrations expose their duration, while
 * timer and range registrations expose the reported time range in the registration time zone.
 * Task, location and hours type are only shown for work time reports.
 */
@Component({
  selector: 'proffeo-time-registration-details',
  templateUrl: './time-registration-details.component.html',
  host: {
    class: 'block w-full'
  },
  providers: [IntlDurationPipe],
  imports: [MatExpansionModule, TranslatePipe]
})
export class TimeRegistrationDetailsComponent {
  private readonly durationPipe = inject(IntlDurationPipe);

  protected readonly detailRows: Signal<TimeRegistrationDetailRow[]> = computed(() => {
    const report = this.timeReport();
    const registration = report?.registration;

    if (!registration) {
      return [];
    }

    const rows: TimeRegistrationDetailRow[] = [
      {
        id: 'date',
        labelKey: 'time-registration-details.date',
        value: formatDateTimeInTimeZone(registration.reportUtcDate, registration.reportTimeZone)
      }
    ];

    const reportedTimeRow = this.buildReportedTimeRow(registration);
    if (reportedTimeRow) {
      rows.push(reportedTimeRow);
    }

    rows.push({
      id: 'registration-type',
      labelKey: 'time-registration-details.registration-type',
      valueKey: registration.registrationType ? REGISTRATION_TYPE_LABEL_KEYS[registration.registrationType] : undefined
    });

    if (report.reportType !== 'WorkTime') {
      return rows;
    }

    rows.push(
      {
        id: 'hours-type',
        labelKey: 'time-registration-details.hours-type',
        valueKey: registration.hoursType ? HOURS_TYPE_LABEL_KEYS[registration.hoursType] : undefined
      },
      {
        id: 'task',
        labelKey: 'time-registration-details.task',
        value: this.formatTask(registration.task?.())
      },
      {
        id: 'location',
        labelKey: 'time-registration-details.location',
        value: registration.location?.()?.name
      }
    );

    return rows;
  });

  public readonly timeReport: InputSignal<TimeReport | undefined> = input<TimeReport | undefined>();

  private buildReportedTimeRow(registration: WorkTimeRegistration): TimeRegistrationDetailRow | undefined {
    if (registration.registrationType === 'Manual') {
      return {
        id: 'duration',
        labelKey: 'time-registration-details.duration',
        value: this.formatDuration(registration.manualHours)
      };
    }

    if (registration.registrationType === 'Timer' || registration.registrationType === 'Range') {
      return {
        id: 'time-range',
        labelKey: 'time-registration-details.time-range',
        value: this.formatTimeRange(registration)
      };
    }

    return undefined;
  }

  private formatTimeRange(registration: WorkTimeRegistration): string | undefined {
    const from = formatTimeInTimeZone(registration.rangeFrom, registration.reportTimeZone);
    const to = formatTimeInTimeZone(registration.rangeTo, registration.reportTimeZone);

    if (!from || !to) {
      return undefined;
    }

    const range = `${from} - ${to}`;
    return registration.reportTimeZone ? `${range} (${registration.reportTimeZone})` : range;
  }

  private formatDuration(isoDuration: string | undefined): string | undefined {
    if (!isoDuration) {
      return undefined;
    }

    const formatted = this.durationPipe.transform(isoDuration);
    return typeof formatted === 'string' ? formatted : undefined;
  }

  private formatTask(task: Task | undefined): string | undefined {
    if (!task) {
      return undefined;
    }

    const taskTypeCode = task.taskType?.()?.code;
    const identifier = taskTypeCode && task.taskNumber ? `${taskTypeCode}-${task.taskNumber}` : task.taskNumber;

    return identifier ? `${identifier}/${task.name}` : task.name;
  }
}
